import { ApiErrorCodeLabelModifier } from '@/api/error-codes';
import {
  Button,
  ConfirmModal,
  Flex,
  Loading,
  Table,
  TableRowMenuCell,
} from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { useMessage } from '@/providers';
import {
  TABLE_CODE_WIDTH,
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  TABLE_YEAR_WIDTH,
  datetimeFormat,
  isRS,
} from '@/utils';
import dayjs from 'dayjs';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  useAccountingTypes,
  useCreateAccountingType,
  useDeleteAccountingType,
  useUpdateAccountingType,
} from '../../hooks';
import {
  AccountingTypeLabelModifier,
  type AccountingTypeModel,
} from '../../models';
import { fillZeroToAccountType } from '../../utils';
import { useDrawerState } from '../DrawerStateProvider';
import { AccountingTypeFormDrawer } from './AccountingTypeFormDrawer';
import { AccountingTypeSortableTable } from './AccountingTypeSortableTable';

export const AccountingTypesPage: FC = () => {
  const { t } = useTranslation();
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const { drawerState, setDrawerState } = useDrawerState();
  const { openMessage } = useMessage();
  const accountingTypes = useAccountingTypes();
  const { mutateAsync: createMutateAsync } = useCreateAccountingType();
  const { mutateAsync: updateMutateAsync } = useUpdateAccountingType();
  const { mutateAsync: deleteMutateAsync } = useDeleteAccountingType();
  const editPermission = useMasterTablePermission(true);
  const [accountingTypeDelete, setAccountingTypeDelete] = useState<z.infer<
    typeof AccountingTypeModel
  > | null>(null);

  const columns = useMemo(
    () => [
      {
        title: t('code'),
        key: 'code',
        dataIndex: 'code',
        width: TABLE_CODE_WIDTH,
        render: fillZeroToAccountType,
      },
      {
        title: t('accounting-type'),
        key: 'type',
        dataIndex: 'type',
        render: (value) => AccountingTypeLabelModifier[value],
      },
      {
        title: t('accounting-name'),
        key: 'name',
        dataIndex: 'name',
      },
      {
        title: t('start-fiscal-year'),
        key: 'startYear',
        dataIndex: 'startYear',
        width: TABLE_YEAR_WIDTH,
      },
      {
        title: t('end-fiscal-year'),
        key: 'endYear',
        dataIndex: 'endYear',
        width: TABLE_YEAR_WIDTH,
      },
      {
        title: t('created-datetime'),
        key: 'createdAt',
        dataIndex: 'createdAt',
        width: TABLE_DATETIME_WIDTH,
        render: (value) => datetimeFormat({ datetime: value }),
      },
      {
        title: t('updated-datetime'),
        key: 'updatedAt',
        dataIndex: 'updatedAt',
        width: TABLE_DATETIME_WIDTH,
        render: (value) => datetimeFormat({ datetime: value }),
      },
      ...(editPermission.data && !isEditingOrder
        ? [
            {
              title: '',
              key: 'actions',
              fixed: 'right' as const,
              width: TABLE_ROW_MENU_WIDTH,
              onCell: () => ({
                style: { minWidth: TABLE_ROW_MENU_WIDTH },
              }),
              render: (_, record) => (
                <TableRowMenuCell
                  onEdit={() => {
                    setDrawerState({
                      open: true,
                      type: 'accounting-type',
                      isEditing: true,
                      id: record.id,
                    });
                  }}
                  items={[
                    {
                      key: 'delete',
                      label: t('delete'),
                      danger: true,
                      onClick: () => setAccountingTypeDelete(record),
                    },
                  ]}
                />
              ),
            },
          ]
        : []),
    ],
    [editPermission.data, setDrawerState, t, isEditingOrder],
  );

  if (accountingTypes.isPending || !accountingTypes.data) {
    return <Loading />;
  }

  const selected = accountingTypes.data.find(
    (d) =>
      drawerState.open &&
      drawerState.type === 'accounting-type' &&
      drawerState.isEditing &&
      d.id === drawerState.id,
  );

  return (
    <>
      {isRS() && editPermission.data && !isEditingOrder && (
        <Flex justify="flex-end" style={{ marginBottom: 12 }}>
          <Button
            onClick={() => {
              setIsEditingOrder((prev) => !prev);
            }}
          >
            {t('sort-order-edit')}
          </Button>
        </Flex>
      )}
      {isEditingOrder ? (
        <AccountingTypeSortableTable
          dataSource={accountingTypes.data}
          columns={columns}
          onCancel={() => {
            setIsEditingOrder(false);
          }}
          onSubmitSuccess={() => {
            setIsEditingOrder(false);
          }}
        />
      ) : (
        <Table
          dataSource={accountingTypes.data}
          rowKey="id"
          columns={columns}
          pagination={false}
        />
      )}

      <AccountingTypeFormDrawer
        {...(drawerState.open && drawerState.isEditing
          ? {
              type: 'edit',
              initialValues: {
                ...selected,
                period: [
                  selected?.startYear
                    ? dayjs().year(selected.startYear)
                    : undefined,
                  selected?.endYear
                    ? dayjs().year(selected.endYear)
                    : undefined,
                ],
              },
            }
          : { type: 'new' })}
        onSubmit={(values) => {
          invariant(drawerState.open, t('unreachable-code'));

          const response = drawerState.isEditing
            ? updateMutateAsync({ ...values, id: drawerState.id })
            : createMutateAsync(values);
          response
            .then(async () => {
              await openMessage('success', t('message-completed'));
              setDrawerState({ open: false });
            })
            .catch(async (error) => {
              const errorCode = error.response?.data.error_code;
              await openMessage(
                'error',
                errorCode === 'BNS_API_COMMON_400_0002_UNIQUE_CONSTRAINT_ERROR'
                  ? t('error-message-projects-accounting-type-constraint')
                  : (ApiErrorCodeLabelModifier[errorCode] ??
                      t('error-message-unknown-error')),
              );
            });
        }}
      />
      <ConfirmModal
        open={accountingTypeDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(accountingTypeDelete, t('unreachable-code'));
          try {
            await deleteMutateAsync(accountingTypeDelete.id);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              setAccountingTypeDelete(null),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => setAccountingTypeDelete(null)}
        skipCheck
      />
    </>
  );
};
